import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  confirmImportRequest,
  getImportReport,
  getImportRequestStatus,
  ImportReportModel,
  ImportReportState,
  ImportRequestMode,
  ImportRequestModel,
} from "../../clients/BackOfficeApi";
import {
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  ListItem,
  Switch,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  UnorderedList,
} from "@chakra-ui/react";
import { BackButton } from "../../elements/BackButton";
import { ImportReportSheet } from "../../components/ImportReportTabContent/ImportReportSheet";
import { Issue } from "../../elements/Issue";
import { SpinnerIcon } from "@chakra-ui/icons";
import { delay } from "../../utils/timerUtils";
import { isApiError } from "../../utils/apiUtils";
import { FormattedMessage } from "react-intl";
import { ChangesSummary } from "../../components/ImportReportTabContent/ChangesSummary";

export function ImportReportDetailPage() {
  const [importReport, setImportReport] = useState<ImportReportModel>();
  const [importRequest, setImportRequest] = useState<ImportRequestModel>();
  const [startingFullRun, setStartingFullRun] = useState(false);
  const [disableNewValueBlocking, setDisableNewValueBlocking] = useState(false);
  const [error, setError] = useState<string>();
  const { importId } = useParams<{ importId: string }>();
  const navigate = useNavigate();

  useEffect(() => {
    if (!importId) return;

    const abortController = new AbortController();

    getImportRequestStatus({
      path: { id: Number(importId) },
      signal: abortController.signal,
      throwOnError: true,
    })
      .then(({ data }) => {
        if (abortController.signal.aborted) return;

        setImportRequest(data);
      })
      .catch((e) => {
        if (abortController.signal.aborted) return;

        setError(e.message ?? String(e));
      });

    return () => abortController.abort("Component unmounted or re-rendered");
  }, [importId]);

  useEffect(() => {
    if (!importRequest) return;

    if (!importRequest.reportId) {
      setError("Report not found");
      return;
    }

    const abortController = new AbortController();

    getImportReport({
      path: { id: importRequest.reportId },
      signal: abortController.signal,
      throwOnError: true,
    })
      .then(({ data }) => {
        if (abortController.signal.aborted) return;

        setImportReport(data);
      })
      .catch((e) => {
        if (abortController.signal.aborted) return;

        setError(e.message ?? String(e));
      });

    return () => abortController.abort("Component unmounted or re-rendered");
  }, [importRequest]);

  const startFullRun = useCallback(() => {
    if (!importRequest?.id) return;

    setStartingFullRun(true);

    delay(200).then(() => {
      confirmImportRequest({
        path: { id: importRequest.id },
        query: { publish: false, disableNewValueBlocking },
        throwOnError: true,
      })
        .then(() => {
          setStartingFullRun(false);

          navigate("../..");
        })
        .catch((e) => {
          if (isApiError(e) && e.status === 409) {
            setError(
              "Er is al een volledige import uitgevoerd voor dit bestand.",
            );
            return;
          }

          setError(e.message ?? String(e));

          setStartingFullRun(false);
        });
    });
  }, [importRequest?.id, navigate, disableNewValueBlocking]);

  const hasBlockedNewValues = importReport?.issues.some((issue) =>
    issue.message.includes("Unexpected"),
  );
  const hasIssuesOtherThanBlockedNewValues = importReport?.issues.some(
    (issue) => !issue.message.includes("Unexpected"),
  );

  return (
    <Box>
      <Box mb={4}>
        <BackButton levels={2} />
      </Box>
      {!!error && (
        <Text color="red.500">
          Er is een fout opgetreden bij het ophalen van het importrapport:{" "}
          {error}
        </Text>
      )}
      {!!importReport && (
        <>
          <Heading as="h1" mb={4}>
            Resultaat voor: {importReport.filename}{" "}
            <Badge
              colorScheme={
                importReport.state === ImportReportState.SUCCEEDED
                  ? "green"
                  : "red"
              }
            >
              <FormattedMessage
                defaultMessage={importReport.state}
                id={`importReport.state.${importReport.state}`}
              />
            </Badge>
          </Heading>
          {importRequest?.mode === ImportRequestMode.DRY_RUN &&
            importReport.state === ImportReportState.SUCCEEDED &&
            !hasIssuesOtherThanBlockedNewValues && (
              <Flex alignItems="center" gap={4} my={2}>
                <Button
                  isDisabled={
                    startingFullRun ||
                    (hasBlockedNewValues && !disableNewValueBlocking)
                  }
                  onClick={startFullRun}
                >
                  Definitief maken {startingFullRun && <SpinnerIcon ml={2} />}
                </Button>

                {hasBlockedNewValues && (
                  <Switch
                    color="orange.500"
                    isChecked={disableNewValueBlocking}
                    onChange={(e) =>
                      setDisableNewValueBlocking(e.target.checked)
                    }
                  >
                    Blokkering nieuwe waarden opheffen voor dit bestand
                  </Switch>
                )}
              </Flex>
            )}
          {importReport.issues.length > 0 && (
            <UnorderedList aria-label="General issues">
              {importReport.issues.map((issue) => (
                <ListItem key={issue.id}>
                  <Issue issue={issue} />
                </ListItem>
              ))}
            </UnorderedList>
          )}
          <Tabs isLazy lazyBehavior="keepMounted" mt={4} variant="enclosed">
            <TabList>
              {importReport.sheets.map((s) => (
                <Tab key={s.id}>
                  {s.name}{" "}
                  {s.issues.length > 0 && <Badge>{s.issues.length}</Badge>}
                </Tab>
              ))}
              {importReport.changeCount > 0 && (
                <Tab key="changes">
                  Wijzigingen aan stamgegevens ({importReport.changeCount})
                </Tab>
              )}
            </TabList>
            <TabPanels>
              {importReport.sheets.map((s) => (
                <TabPanel key={s.id}>
                  <ImportReportSheet sheet={s} />
                </TabPanel>
              ))}
              {importReport.changeCount > 0 && (
                <TabPanel key="changes">
                  <ChangesSummary reportId={importReport.id} />
                </TabPanel>
              )}
            </TabPanels>
          </Tabs>
        </>
      )}
    </Box>
  );
}
